import { forwardRef, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../../lib/cn";

type PanelElement = "article" | "div" | "section";
type PanelSectionElement = "div" | "section";

type PanelProps = HTMLAttributes<HTMLElement> & {
  as?: PanelElement;
};

export function Panel({ as: Component = "div", className, ...props }: PanelProps) {
  return <Component className={cn("ui-panel", className)} {...props} />;
}

export function PanelHeader({
  title,
  meta,
  action,
  className,
  as: Heading = "h2"
}: {
  title: string;
  meta?: ReactNode;
  action?: ReactNode;
  className?: string;
  as?: "h2" | "h3";
}) {
  return (
    <div className={cn("ui-panel-header", className)}>
      <div>
        <Heading>{title}</Heading>
        {meta && <span className="ui-panel-header-meta">{meta}</span>}
      </div>
      {action}
    </div>
  );
}

export function PanelSection({ as: Component = "div", className, ...props }: HTMLAttributes<HTMLElement> & { as?: PanelSectionElement }) {
  return <Component className={cn("ui-panel-section", className)} {...props} />;
}

export function PanelNote({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn("ui-panel-note", className)} {...props} />;
}

export const FocusRegion = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, tabIndex = -1, ...props }, ref) => <div ref={ref} tabIndex={tabIndex} className={cn("ui-focus-region", className)} {...props} />
);

FocusRegion.displayName = "FocusRegion";

export function SectionLabel({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn("ui-section-label", className)} {...props} />;
}
